import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {Component, ExpressionPath, Expressions} from '@packages/systems/wfdl';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

function getPropFromSelect(maybeSelect?: Expression): string | null {
  const select = maybeSelect ? Expressions.getSelect(maybeSelect) : null;
  const variable = select ? Expressions.getVariable(select.from) : null;
  if (select && variable) {
    return select.prop;
  }
  return null;
}

/**
 * Given an element, if it has its children set to select from a
 * prop, return the propId for that prop.
 */
export function getInstanceChildrenPropId(
  element: ExpressionElement
): string | null {
  const data = Expressions.getElementData(element);
  const children = Expressions.getIn(data, CHILDREN_PATH);
  return getPropFromSelect(children);
}

function findMappedPropId(propId: string, element: ExpressionElement): string {
  const data = Expressions.getElementData(element);
  const record = data ? Expressions.getRecord(data) : null;

  if (!record || !record[propId]) {
    return propId;
  }

  const newPropId = getPropFromSelect(record[propId]);
  return newPropId || propId;
}

/**
 * Given a nested component's propId, find the propId for the
 * currently selected element which sets the nested component's prop.
 * The nativeIdPath is the path to the currently selected element.
 */
export function getPropIdForNestedComponent(
  propId: string,
  nativeIdPath: NativeId[],
  componentRender: Expression,
  componentMap: Component.ComponentMap
): string {
  let currentComponentRender = componentRender;
  const elements: ExpressionElement[] = [];
  // We don't need to get the last element because we already have
  // the propId from the last element as an argument
  nativeIdPath.slice(0, -1).forEach((nativeId) => {
    const element = Expressions.getElementById(
      currentComponentRender,
      nativeId
    );
    if (!element) {
      return;
    }
    elements.push(element);
    const component = componentMap.get(element.type);
    if (!component) {
      return;
    }
    currentComponentRender = component.render;
  });
  return elements.reduceRight<string>(findMappedPropId, propId);
}
